import { CSSProperties, FC, JSX, useCallback, useEffect, useRef, useState } from "react";
import Portal from "@app/shared/Portal";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

/**
 * Module-level reference to the currently open popover's close function.
 * Ensures only one InfoPopover is open at a time — clicking a second "i" icon
 * automatically closes the first before opening the second.
 */
let currentlyOpenClose: (() => void) | null = null;

const HORIZONTAL_OFFSET = 8;
const VERTICAL_EDGE_THRESHOLD = 120;

/**
 * Where the popover is rendered relative to the trigger icon.
 * Drives both the inline position (top/left/bottom) and which side of the
 * popover the directional arrow points from.
 */
type Placement = "bottom" | "right" | "top";

interface PopoverPositionInfo {
  placement: Placement;
  style: CSSProperties;
}

export interface InfoPopoverProps {
  /** JSX or plain text to render inside the popover body. */
  content: JSX.Element | string;
  /** Additional CSS class(es) for the trigger button. */
  triggerClassName?: string;
  /** data-testid for the trigger button. */
  triggerTestId?: string;
}

/**
 * An information popover triggered by clicking an "i" icon.
 *
 * Interaction rules:
 * - Opens only on click (not hover).
 * - Closes only when the user clicks the "X" button, or when a second InfoPopover is opened.
 * - Clicking outside the popover or pressing Escape does not close it.
 * - Only one InfoPopover may be open at a time across the page.
 *
 * Positioning:
 * - Default: opens to the right of the icon, top edge aligned with the containing row.
 * - Near the top edge of the viewport: opens below the icon.
 * - Near the bottom edge of the viewport: opens above the icon.
 */
export const InfoPopover: FC<InfoPopoverProps> = ({ content, triggerClassName, triggerTestId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [positionInfo, setPositionInfo] = useState<PopoverPositionInfo>({
    placement: "right",
    style: { left: 0, top: 0 },
  });
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback((): void => {
    setIsOpen(false);
    if (currentlyOpenClose === close) {
      currentlyOpenClose = null;
    }
  }, []);

  const computePosition = useCallback((): PopoverPositionInfo => {
    const trigger = triggerRef.current;
    if (!trigger) return { placement: "right", style: { left: 0, top: 0 } };

    const iconRect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    if (iconRect.top < VERTICAL_EDGE_THRESHOLD) {
      // Near the top edge: open below the icon
      return {
        placement: "bottom",
        style: { left: iconRect.left, top: iconRect.bottom + 4 },
      };
    }

    if (iconRect.bottom > viewportHeight - VERTICAL_EDGE_THRESHOLD) {
      // Near the bottom edge: open above the icon (anchor via CSS "bottom")
      return {
        placement: "top",
        style: { bottom: viewportHeight - iconRect.top + 4, left: iconRect.left },
      };
    }

    // Normal case: open to the right of the icon, top-aligned with the containing row
    let rowTop = iconRect.top;
    let el: HTMLElement | null = trigger.parentElement;
    while (el) {
      if (el.classList.contains("provider-profile__network-integrity-row")) {
        rowTop = el.getBoundingClientRect().top;
        break;
      }
      el = el.parentElement;
    }

    return {
      placement: "right",
      style: { left: iconRect.right + HORIZONTAL_OFFSET, top: rowTop },
    };
  }, []);

  const handleTriggerClick = useCallback((): void => {
    // Close any other open popover first
    if (currentlyOpenClose && currentlyOpenClose !== close) {
      currentlyOpenClose();
    }

    if (isOpen) {
      close();
      return;
    }

    setPositionInfo(computePosition());
    setIsOpen(true);
    currentlyOpenClose = close;
  }, [close, computePosition, isOpen]);

  useEffect(() => {
    return () => {
      if (currentlyOpenClose === close) {
        currentlyOpenClose = null;
      }
    };
  }, [close]);

  // Reposition the popover on scroll so it stays anchored to the trigger.
  useEffect(() => {
    if (!isOpen) return;

    let rafId: number;

    const handleScroll = (): void => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setPositionInfo(computePosition());
      });
    };

    window.addEventListener("scroll", handleScroll, { capture: true });

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      cancelAnimationFrame(rafId);
    };
  }, [isOpen, computePosition]);

  return (
    <>
      <button
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="More information"
        className={`info-popover__trigger${triggerClassName ? ` ${triggerClassName}` : ""}`}
        data-testid={triggerTestId}
        onClick={handleTriggerClick}
        type="button"
      >
        <QesIcon iconName={QesIcons.InfoCircle} isDecorative />
      </button>
      {isOpen &&
        <Portal hideBodyScroll={false}>
          <div
            aria-label="Information"
            aria-modal="false"
            className={`info-popover info-popover--placement-${positionInfo.placement}`}
            role="dialog"
            style={positionInfo.style}
          >
            <button
              aria-label="Close"
              className="info-popover__close"
              onClick={close}
              type="button"
            >
              <QesIcon iconName={QesIcons.X} isDecorative />
            </button>
            <div className="info-popover__content">
              {content}
            </div>
          </div>
        </Portal>
      }
    </>
  );
};
